import { useState, useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { TermsContent } from '@/pages/legal/TermsContent'
import { PrivacyContent } from '@/pages/legal/PrivacyContent'

interface BasicInfoData {
  name: string
  email: string
  cpf: string
  username: string
  phone: string
  city: string
  state: string
  profession: string
  howKnew: string
  aiFamiliarity: string
  termsAccepted: boolean
}

interface BasicInfoStepProps {
  data?: Partial<BasicInfoData>
  onChange: (data: Partial<BasicInfoData>) => void
  onValidationChange: (isValid: boolean) => void
  errors?: Record<string, string>
}

const states = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO',
  'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI',
  'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
]

const howKnewOptions = [
  { id: 'gaas', label: 'GAAS' },
  { id: 'indication', label: 'Indicação' },
  { id: 'event', label: 'Evento' },
  { id: 'social_media', label: 'Redes sociais' },
  { id: 'website', label: 'Site' },
  { id: 'other', label: 'Outro' }
]

const aiFamiliarityOptions = [
  { id: 'never', label: 'Nunca usei' },
  { id: 'occasionally', label: 'Uso ocasionalmente' },
  { id: 'frequently', label: 'Uso com frequência' }
]

export function BasicInfoStep({ data, onChange, onValidationChange, errors }: BasicInfoStepProps) {
  const [cpfError, setCpfError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [usernameError, setUsernameError] = useState('')
  const [termsScrolled, setTermsScrolled] = useState(false)
  const [privacyScrolled, setPrivacyScrolled] = useState(false)
  const termsRef = useRef<HTMLDivElement>(null)
  const privacyRef = useRef<HTMLDivElement>(null)

  const handleDocScroll = (
    ref: React.RefObject<HTMLDivElement>,
    setter: (v: boolean) => void,
    already: boolean
  ) => {
    if (already || !ref.current) return
    const { scrollTop, scrollHeight, clientHeight } = ref.current
    if (scrollTop + clientHeight >= scrollHeight - 24) {
      setter(true)
    }
  }

  // Validação de CPF
  const validateCPFAlgorithm = (cpf: string) => {
    const cleanCPF = cpf.replace(/[^\d]/g, '')

    if (cleanCPF.length !== 11) return false

    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(cleanCPF)) return false

    // Calcula dígitos verificadores
    let sum = 0
    let remainder

    // Primeiro dígito verificador
    for (let i = 1; i <= 9; i++) {
      sum += parseInt(cleanCPF.substring(i - 1, i)) * (11 - i)
    }
    remainder = (sum * 10) % 11
    if (remainder === 10 || remainder === 11) remainder = 0
    if (remainder !== parseInt(cleanCPF.substring(9, 10))) return false

    // Segundo dígito verificador
    sum = 0
    for (let i = 1; i <= 10; i++) {
      sum += parseInt(cleanCPF.substring(i - 1, i)) * (12 - i)
    }
    remainder = (sum * 10) % 11
    if (remainder === 10 || remainder === 11) remainder = 0
    if (remainder !== parseInt(cleanCPF.substring(10, 11))) return false

    return true
  }

  const validateCPF = (cpf: string) => {
    const cleanCPF = cpf.replace(/[^\d]/g, '')

    if (cleanCPF.length !== 11) {
      setCpfError('CPF deve ter 11 dígitos')
      return
    }

    // Validação algorítmica completa
    if (!validateCPFAlgorithm(cpf)) {
      setCpfError('CPF inválido')
      return
    }

    setCpfError('')
  }

  // Validação de email
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setEmailError('Email inválido')
      return false
    }
    setEmailError('')
    return true
  }

  // Validação de username
  const validateUsername = (username: string) => {
    if (username.length < 3 || username.length > 20) {
      setUsernameError('Username deve ter 3-20 caracteres')
      return false
    }

    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setUsernameError('Username deve conter apenas letras, números e underscore')
      return false
    }

    setUsernameError('')
    return true
  }

  // Formatar CPF
  const formatCPF = (value: string) => {
    const cleanValue = value.replace(/\D/g, '')
    const formatted = cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    return formatted.slice(0, 14)
  }

  // Formatar telefone
  const formatPhone = (value: string) => {
    const cleanValue = value.replace(/\D/g, '')
    if (cleanValue.length <= 11) {
      return cleanValue.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
    }
    return value.slice(0, 15)
  }

  // Validacao geral
  //
  // Revalidamos email/CPF/username diretamente a partir do `data` em vez de
  // depender apenas das flags de erro (que iniciam vazias). Isso evita o caso
  // em que o formulario e restaurado/preenchido programaticamente sem disparar
  // os handlers de mudanca, deixando flags vazias e step indevidamente valido.
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const cpfClean = (data?.cpf || '').replace(/\D/g, '')
    const usernameValue = data?.username || ''

    const emailValid = !!data?.email && emailRegex.test(data.email)
    const cpfValid = cpfClean.length === 11 && validateCPFAlgorithm(data?.cpf || '')
    const usernameValid =
      usernameValue.length >= 3 &&
      usernameValue.length <= 20 &&
      /^[a-zA-Z0-9_]+$/.test(usernameValue)

    const requiredFields: (keyof BasicInfoData)[] = [
      'name', 'email', 'cpf', 'username', 'city', 'state', 'profession', 'howKnew', 'aiFamiliarity'
    ]
    const allRequiredFilled = requiredFields.every(field => data?.[field])

    const isValid =
      allRequiredFilled &&
      emailValid &&
      cpfValid &&
      usernameValid &&
      (data?.termsAccepted ?? false) &&
      termsScrolled &&
      privacyScrolled &&
      !cpfError &&
      !emailError &&
      !usernameError

    onValidationChange(isValid)
  }, [data, cpfError, emailError, usernameError, termsScrolled, privacyScrolled])

  const handleFieldChange = (field: keyof BasicInfoData, value: any) => {
    onChange({ [field]: value })

    // Validações específicas
    if (field === 'cpf') {
      const formatted = formatCPF(value)
      onChange({ cpf: formatted })
      validateCPF(formatted)
      return
    } else if (field === 'email') {
      validateEmail(value)
    } else if (field === 'username') {
      validateUsername(value)
    } else if (field === 'phone') {
      const formatted = formatPhone(value)
      onChange({ phone: formatted })
      return
    }
  }


  return (
    <div className="space-y-6">
      {/* Alerta de erros de validação */}
      {errors && Object.keys(errors).length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex items-start">
            <X className="text-red-500 mt-0.5 mr-3" size={20} />
            <div>
              <h3 className="text-sm font-semibold text-red-800 mb-2">
                Dados já cadastrados
              </h3>
              <ul className="text-sm text-red-700 space-y-1">
                {errors.email && <li>• {errors.email}</li>}
                {errors.cpf && <li>• {errors.cpf}</li>}
                {errors.username && <li>• {errors.username}</li>}
              </ul>
              <p className="text-sm text-red-700 mt-2">
                Por favor, corrija os campos destacados abaixo.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Nome */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-brand-text mb-2">
            Nome Completo *
          </label>
          <input
            type="text"
            value={data?.name || ''}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            className="input-field"
            placeholder="Seu nome completo"
          />
          {errors?.name && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <X size={14} />
              {errors.name}
            </p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            E-mail *
          </label>
          <input
            type="email"
            value={data?.email || ''}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            className={cn("input-field", emailError && "border-red-500 focus:ring-red-500")}
            placeholder="seu@email.com"
          />
          {(emailError || errors?.email) && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <X size={14} />
              {errors?.email || emailError}
            </p>
          )}
        </div>

        {/* CPF */}
        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            CPF *
          </label>
          <input
            type="text"
            value={data?.cpf || ''}
            onChange={(e) => handleFieldChange('cpf', e.target.value)}
            className={cn("input-field", cpfError && "border-red-500 focus:ring-red-500")}
            placeholder="000.000.000-00"
            maxLength={14}
          />
          {(cpfError || errors?.cpf) && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <X size={14} />
              {errors?.cpf || cpfError}
            </p>
          )}
        </div>

        {/* Username */}
        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Nome de Usuário *
          </label>
          <div className="relative">
            <input
              type="text"
              value={data?.username || ''}
              onChange={(e) => handleFieldChange('username', e.target.value)}
              className={cn("input-field pr-8", usernameError && "border-red-500 focus:ring-red-500")}
              placeholder="usuario_unico"
              maxLength={20}
            />
          </div>
          {(usernameError || errors?.username) && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <X size={14} />
              {errors?.username || usernameError}
            </p>
          )}
          <p className="mt-1 text-xs text-brand-text-muted">
            3-20 caracteres, letras, números e underscore
          </p>
        </div>

        {/* Telefone */}
        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Telefone / WhatsApp
          </label>
          <input
            type="text"
            value={data?.phone || ''}
            onChange={(e) => handleFieldChange('phone', e.target.value)}
            className="input-field"
            placeholder="(00) 00000-0000"
            maxLength={15}
          />
        </div>

        {/* Cidade */}
        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Cidade *
          </label>
          <input
            type="text"
            value={data?.city || ''}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            className="input-field"
            placeholder="Sua cidade"
          />
        </div>

        {/* Estado */}
        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Estado (UF) *
          </label>
          <select
            value={data?.state || ''}
            onChange={(e) => handleFieldChange('state', e.target.value)}
            className="input-field"
          >
            <option value="">Selecione...</option>
            {states.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        </div>

        {/* Profissão */}
        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Profissão / Área de atuação *
          </label>
          <input
            type="text"
            value={data?.profession || ''}
            onChange={(e) => handleFieldChange('profession', e.target.value)}
            className="input-field"
            placeholder="Sua profissão"
          />
        </div>

        {/* Como conheceu */}
        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Como você conheceu o RegenerAI? *
          </label>
          <select
            value={data?.howKnew || ''}
            onChange={(e) => handleFieldChange('howKnew', e.target.value)}
            className="input-field"
          >
            <option value="">Selecione...</option>
            {howKnewOptions.map(option => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </div>

        {/* Familiaridade com IA */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-brand-text mb-2">
            Nível de familiaridade com inteligência artificial *
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {aiFamiliarityOptions.map(option => (
              <button
                key={option.id}
                type="button"
                onClick={() => handleFieldChange('aiFamiliarity', option.id)}
                className={cn(
                  "p-3 rounded-lg border-2 text-left transition-all",
                  data?.aiFamiliarity === option.id
                    ? "border-brand-primary bg-brand-primary/5"
                    : "border-brand-border bg-brand-surface hover:border-brand-primary/50"
                )}
              >
                <span className="text-brand-text">{option.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Termos de uso — leitura obrigatória */}
        <div className="md:col-span-2 space-y-5">

          {/* Termos de Uso */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-brand-text">
                Termos de Uso *
              </p>
              {termsScrolled ? (
                <span className="text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 px-2 py-0.5 rounded">
                  Lido
                </span>
              ) : (
                <span className="text-xs text-brand-text-muted">
                  Role até o fim para continuar
                </span>
              )}
            </div>
            <div
              ref={termsRef}
              className={cn(
                "h-52 overflow-y-auto border rounded-lg p-4 bg-brand-surface",
                termsScrolled ? "border-green-300" : "border-brand-border"
              )}
              onScroll={() => handleDocScroll(termsRef, setTermsScrolled, termsScrolled)}
            >
              <TermsContent />
            </div>
          </div>

          {/* Política de Privacidade */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-brand-text">
                Política de Privacidade *
              </p>
              {privacyScrolled ? (
                <span className="text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 px-2 py-0.5 rounded">
                  Lida
                </span>
              ) : (
                <span className="text-xs text-brand-text-muted">
                  Role até o fim para continuar
                </span>
              )}
            </div>
            <div
              ref={privacyRef}
              className={cn(
                "h-52 overflow-y-auto border rounded-lg p-4 bg-brand-surface",
                privacyScrolled ? "border-green-300" : "border-brand-border"
              )}
              onScroll={() => handleDocScroll(privacyRef, setPrivacyScrolled, privacyScrolled)}
            >
              <PrivacyContent />
            </div>
          </div>

          {/* Checkbox de aceite */}
          <div>
            <label className={cn(
              "flex items-start gap-3",
              (!termsScrolled || !privacyScrolled) ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
            )}>
              <input
                type="checkbox"
                checked={data?.termsAccepted || false}
                disabled={!termsScrolled || !privacyScrolled}
                onChange={(e) => handleFieldChange('termsAccepted', e.target.checked)}
                className="mt-1 w-4 h-4 text-brand-primary border-brand-border rounded focus:ring-brand-primary disabled:cursor-not-allowed"
              />
              <span className="text-sm text-brand-text">
                Li e aceito os Termos de Uso e a Política de Privacidade *
              </span>
            </label>
            {(!termsScrolled || !privacyScrolled) && (
              <p className="mt-1.5 text-xs text-brand-text-muted">
                Role os dois documentos acima até o fim para habilitar esta opção.
              </p>
            )}
          </div>

        </div>
      </div>


    </div>
  )
}
